import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  Typography, 
  Button, 
  Card,
  Row, 
  Col, 
  Alert, 
  Spin, 
  Image, 
  message, 
  Modal,
  Input 
} from "antd";
import { 
  PictureOutlined,
  CameraOutlined,
  CloudDownloadOutlined,
  CheckOutlined,
  UploadOutlined,
  LockFilled,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  EyeOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import { useGetEventByHashQuery, useFindMatchingPhotosMutation, useGetEventPublicPhotosQuery } from "../services/nestjs-base-api";
import type { RcFile, IMatchingPhoto } from "../services/nestjs-base-api/types";
import { PhotoProcessingStatusEnum } from "../services/nestjs-base-api/types";

// Create a custom type to handle the webcam capture
interface WebcamFile extends File {
  uid: string;
  lastModifiedDate?: Date;
}

const { Text, Paragraph } = Typography;

const MobileContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%);
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    padding: 20px;
    align-items: center;
    justify-content: center;
  }
`;

const MobileHeader = styled.div`
  padding: 16px 24px 8px 24px;
  text-align: center;
  color: white;
  
  @media (min-width: 768px) {
    padding: 20px 24px;
  }
`;

const MobileBrand = styled.h1`
  font-size: 40px;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: white;
  letter-spacing: -1px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const MobileSubtitle = styled.p`
  font-size: 15px;
  margin: 0 auto;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
  max-width: 400px;
`;

const DesignerCredit = styled.div`
  margin-top: 12px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 4px;

  a {
    color: white;
    font-weight: 600;
    font-size: 13px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CreditDivider = styled.span`
  color: rgba(255, 255, 255, 0.7);

  @media (max-width: 400px) {
    display: none;
  }
`;

const TabContainer = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  padding: 4px;
  margin: 12px 24px 0 24px;
  backdrop-filter: blur(10px);
  
  @media (min-width: 768px) {
    max-width: 400px;
    margin: 20px auto 0 auto;
  }
`;

const TabButton = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.active ? `
    background: white;
    color: #6f4cff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  ` : `
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      color: white;
    }
  `}
`;

const ContentCard = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  margin-top: 20px;
  padding: 24px 20px;
  flex: 1;
  
  @media (min-width: 768px) {
    border-radius: 24px;
    margin: 32px 24px 24px 24px;
    max-width: 500px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    padding: 32px 24px;
  }
`;

const PrivacyNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #f0f9ff;
  border-radius: 16px;
  margin-bottom: 20px;
  border: 1px solid #e0f2fe;
  
  @media (min-width: 768px) {
    gap: 16px;
    padding: 20px;
    margin-bottom: 32px;
  }
`;

const PrivacyIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    font-size: 24px;
  }
`;

const UploadSection = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 24px 20px;
  text-align: center;
  position: relative;
  
  @media (min-width: 768px) {
    padding: 40px 24px;
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 16px;
  
  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const GalleryImageContainer = styled.div`
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  
  @media (min-width: 768px) {
    min-height: 150px;
    height: 150px;
    align-items: stretch;
    background: transparent;
  }
`;

const GalleryImage = styled(Image)`
  width: 100%;
  height: auto !important;
  object-fit: contain;
  
  @media (min-width: 768px) {
    height: 150px !important;
    object-fit: cover;
  }
`;


const StyledActionButton = styled(Button)<{ variant?: 'primary' | 'secondary' }>`
  height: 56px;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
    }
  ` : `
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
    
    &:hover {
      background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(139, 92, 246, 0.4);
    }
  `}
`;


// Helper function to check if photo is ready for viewing
const isPhotoReady = (photo: any): boolean => {
  return !photo.processingStatus || photo.processingStatus === PhotoProcessingStatusEnum.Complete;
};

// Helper function to get thumbnail source or default
const getThumbnailSource = (photo: any): string | null => {
  if (isPhotoReady(photo) && photo.thumbnailUrl) {
    return photo.thumbnailUrl;
  }
  return null;
};

// Helper function to get processing status display for public photos
const getProcessingStatusDisplay = (photo: any) => {
  if (!photo.processingStatus || photo.processingStatus === PhotoProcessingStatusEnum.Complete) {
    return null;
  }
  
  switch (photo.processingStatus) {
    case PhotoProcessingStatusEnum.Processing:
      return {
        icon: <LoadingOutlined spin />,
        text: 'Processing...',
        color: '#1890ff'
      };
    case PhotoProcessingStatusEnum.Failed:
      return {
        icon: <ExclamationCircleOutlined />,
        text: 'Failed to process',
        color: '#ff4d4f'
      };
    default:
      return null;
  }
};

const EventPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { eventHash } = useParams<{ eventHash?: string }>();
  const [selfieFile, setSelfieFile] = React.useState<RcFile | null>(null);
  const [selfieUrl, setSelfieUrl] = React.useState<string | null>(null);
  const [matchingPhotos, setMatchingPhotos] = React.useState<IMatchingPhoto[]>([]);
  const [isResultsModalVisible, setIsResultsModalVisible] = React.useState(false);
  const [isCameraModalVisible, setIsCameraModalVisible] = React.useState(false);
  const [isGalleryModalVisible, setIsGalleryModalVisible] = React.useState(false);
  const [stream, setStream] = React.useState<MediaStream | null>(null);
  const [isMobile, setIsMobile] = React.useState(false);
  const [eventCode, setEventCode] = React.useState<string>('');
  const firstGalleryImageRef = React.useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = React.useState<'search' | 'gallery'>('search');
  const [isDownloadingAllGallery, setIsDownloadingAllGallery] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [messageApi, contextHolder] = message.useMessage();
  
  // RTK Query mutation for finding matching photos
  const [findMatchingPhotos, { isLoading: isMatching }] = useFindMatchingPhotosMutation();
  
  // RTK Query for fetching public photos
  const { data: publicPhotos, isLoading: isLoadingPublicPhotos, error: publicPhotosError } = useGetEventPublicPhotosQuery(
    { eventHash: eventHash || '' },
    { skip: !eventHash }
  );
  
  // Fetch event data if hash is available
  const { data: event, isLoading, isError } = useGetEventByHashQuery(
    { hash: eventHash || '' }, 
    { skip: !eventHash }
  );
  
  // Function to find event by code
  const handleFindEvent = () => {
    if (!eventCode.trim()) {
      messageApi.error('Please enter an event code');
      return;
    }
    
    messageApi.loading('Finding your event...', 0.5);
    
    // Navigate to the event page with the provided event code
    navigate(`/event/${eventCode.trim()}`);
  };
  
  // Handle file selection before upload
  const handleFileChange = ({ fileList }: { fileList: any[] }) => {
    if (fileList.length > 0) {
      const file = fileList[0].originFileObj;
      setSelfieFile(file);

      // Create and store URL for the image
      if (selfieUrl) {
        URL.revokeObjectURL(selfieUrl); // Clean up previous URL
      }
      const url = URL.createObjectURL(file);
      setSelfieUrl(url);

      // Scroll to and emphasize the "Find Your Photos" button after a short delay
      setTimeout(() => {
        const findPhotosButton = document.querySelector('.find-photos-button');
        if (findPhotosButton) {
          findPhotosButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add animation class to draw attention to the button
          findPhotosButton.classList.add('pulse-animation');
        }
      }, 800);
    } else {
      if (selfieUrl) {
        URL.revokeObjectURL(selfieUrl); // Clean up URL when file is removed
      }
      setSelfieFile(null);
      setSelfieUrl(null);
    }
  };
  
  // Function to reset the uploaded image
  const handleRemoveImage = () => {
    if (selfieUrl) {
      URL.revokeObjectURL(selfieUrl);
    }
    setSelfieFile(null);
    setSelfieUrl(null);
  };
  
  // Function to find matching photos
  const handleFindPhotos = async () => {
    if (!selfieFile || !event?.id) {
      messageApi.error('Please upload a selfie first');
      return;
    }
    
    messageApi.loading('Finding photos that match your selfie...', 0);
    
    try {
      const result = await findMatchingPhotos({
        eventId: event.id,
        selfie: selfieFile
      }).unwrap();
      
      setMatchingPhotos(result);
      setIsResultsModalVisible(true);
      messageApi.destroy(); // Clear the loading message
      messageApi.success(`Found ${result.length} matching photos!`);
    } catch (error) {
      console.error('Error finding matching photos:', error);
      messageApi.destroy(); // Clear the loading message
      messageApi.error('Failed to find matching photos. Please try again.');
    }
  };
  
  // Function to download a single photo using the API proxy route
  const handleDownloadPhoto = async (photo: IMatchingPhoto) => {
    const key = 'singleDownloadProgress';
    messageApi.loading({ content: 'Preparing download...', key, duration: 0 });
    
    try {
      // Get API base URL, ensuring it doesn't end with a slash
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const apiBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      // Use the downloadUrl from the API response with the API base URL
      const proxyUrl = `${apiBaseUrl}${photo.downloadUrl}`;
      
      // Fetch the image through the proxy
      const response = await fetch(proxyUrl);
      const blob = await response.blob();
      
      // Extract file name from URL for the download attribute (fallback to photo ID if needed)
      const urlParts = photo.signedUrl.split('/');
      const lastPart = urlParts[urlParts.length - 1];
      const fileName = lastPart.split('?')[0].split('-').slice(1).join('-') || `photo_${photo.itemId}.jpg`;
      
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(link.href);
      
      messageApi.success({ content: 'Photo downloaded successfully!', key });
    } catch (error) {
      console.error('Error downloading photo:', error);
      messageApi.error({ content: 'Failed to download photo. Please try again.', key });
    }
  };
  
  // Function to download a public photo
  const handleDownloadPublicPhoto = async (photo: any) => {
    const key = 'publicPhotoDownload';
    messageApi.loading({ content: 'Preparing download...', key, duration: 0 });
    
    try {
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = photo.url;
      link.download = photo.filename || `photo_${photo.id}.jpg`;
      link.target = '_blank';
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      messageApi.success({ content: 'Photo downloaded successfully!', key });
    } catch (error) {
      console.error('Error downloading photo:', error);
      messageApi.error({ content: 'Failed to download photo. Please try again.', key });
    }
  };

  // Function to download all matching photos using the API proxy route
  const handleDownloadAll = async () => {
    if (matchingPhotos.length === 0) return;
    
    const key = 'downloadProgress';
    messageApi.loading({ content: 'Preparing download of all photos...', key, duration: 0 });
    
    try {
      // Get API base URL, ensuring it doesn't end with a slash
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const apiBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      for (let i = 0; i < matchingPhotos.length; i++) {
        const photo = matchingPhotos[i];
        
        // Update progress message
        const progress = Math.round(((i + 1) / matchingPhotos.length) * 100);
        messageApi.loading({ 
          content: `Downloading photo ${i + 1} of ${matchingPhotos.length} (${progress}%)`, 
          key, 
          duration: 0 
        });
        
        // Use the downloadUrl from the API response with the API base URL
        const proxyUrl = `${apiBaseUrl}${photo.downloadUrl}`;
        
        // Fetch the image through the proxy
        const response = await fetch(proxyUrl);
        const blob = await response.blob();
        
        // Extract file name from URL for the download attribute (fallback to photo ID if needed)
        const urlParts = photo.signedUrl.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        const fileName = lastPart.split('?')[0].split('-').slice(1).join('-') || `photo_${photo.itemId}.jpg`;
        
        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        URL.revokeObjectURL(link.href);
        
        // Wait a short time between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      messageApi.success({ content: 'All photos downloaded successfully!', key });
    } catch (error) {
      console.error('Error downloading photos:', error);
      messageApi.error({ content: 'Failed to download some photos. Please try again.', key });
    }
  };
  
  // Function to download all gallery photos using the event endpoint
  const handleDownloadAllGalleryPhotos = async () => {
    if (!eventHash || !publicPhotos || publicPhotos.length === 0) return;
    
    // Check if there are any completed photos to download
    const completedPhotos = publicPhotos.filter(photo => isPhotoReady(photo));
    if (completedPhotos.length === 0) {
      messageApi.warning('No completed photos to download');
      return;
    }
    
    const key = 'downloadAllGalleryProgress';
    setIsDownloadingAllGallery(true);
    messageApi.loading({ content: 'Preparing download...', key, duration: 0 });
    
    try {
      // Get API base URL
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const apiBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      // Use the event download endpoint with eventHash for security
      const downloadUrl = `${apiBaseUrl}/galleries/event/${eventHash}/download-all`;
      const response = await fetch(downloadUrl, {
        method: 'GET'
      });
      
      if (!response.ok) {
        let errorMessage = 'Download failed';
        if (response.status === 404) {
          errorMessage = 'No photos available for download';
        } else if (response.status === 401) {
          errorMessage = 'Unauthorized access';
        }
        messageApi.error({ content: errorMessage, key });
        return;
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `${event?.name || 'Event'}_photos.zip`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      URL.revokeObjectURL(link.href);
      
      messageApi.success({ 
        content: `Successfully downloaded ${completedPhotos.length} photos!`, 
        key 
      });
    } catch (error: any) {
      console.error('Error downloading gallery photos:', error);
      
      messageApi.error({ 
        content: error.message || 'Failed to download photos. Please try again.', 
        key 
      });
    } finally {
      setIsDownloadingAllGallery(false);
    }
  };
  
  
  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Open camera modal
  const handleOpenCamera = async () => {
    try {
      setIsCameraModalVisible(true);
      
      // Attempt to access the camera with appropriate constraints
      const constraints = isMobile 
        ? { video: { facingMode: "user" } } // Front camera on mobile
        : { video: true }; // Any camera on desktop
        
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      // Attach the stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      messageApi.error('Unable to access camera. Please check your camera permissions.');
      setIsCameraModalVisible(false);
    }
  };
  
  // Close camera modal and release resources
  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraModalVisible(false);
  };
  
  // Take a photo using the webcam
  const handleCapturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame on the canvas
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            try {
              // Create a File object from the blob
              const file = new File([blob], "webcam-selfie.jpg", {
                type: "image/jpeg",
                lastModified: Date.now()
              });

              // Create a custom object that extends the File
              const customFile: WebcamFile = Object.assign(file, {
                uid: Date.now().toString()
              });

              // Use our existing file handler
              if (selfieUrl) {
                URL.revokeObjectURL(selfieUrl);
              }

              setSelfieFile(customFile as unknown as RcFile);
              const url = URL.createObjectURL(blob);
              setSelfieUrl(url);

              // Close the camera modal
              handleCloseCamera();
              messageApi.success('Selfie captured successfully!');

              // Scroll to and emphasize the "Find Your Photos" button after a short delay
              setTimeout(() => {
                const findPhotosButton = document.querySelector('.find-photos-button');
                if (findPhotosButton) {
                  findPhotosButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  // Add animation class to draw attention to the button
                  findPhotosButton.classList.add('pulse-animation');
                }
              }, 800);
            } catch (error) {
              console.error('Error creating file:', error);
              messageApi.error('Failed to capture photo. Please try again.');
              handleCloseCamera();
            }
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };
  
  // Clean up resources when component unmounts
  React.useEffect(() => {
    return () => {
      if (selfieUrl) {
        URL.revokeObjectURL(selfieUrl);
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [selfieUrl, stream]);

  const renderGalleryContent = () => {
    if (isLoadingPublicPhotos) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '60px 20px',
          color: '#6b7280'
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', fontSize: '16px', fontWeight: 500 }}>
            Loading gallery photos...
          </div>
        </div>
      );
    }

    if (publicPhotosError || !publicPhotos) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#6b7280'
        }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '20px',
            opacity: 0.5
          }}>
            📷
          </div>
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 600, 
            marginBottom: '12px',
            color: '#1f2937'
          }}>
            Gallery not available
          </div>
          <div style={{ fontSize: '15px', lineHeight: '1.5' }}>
            Public photos for this event are not available at the moment.
            <br />
            Try using the Search tab to find your photos instead.
          </div>
        </div>
      );
    }

    if (publicPhotos.length === 0) {
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#6b7280'
        }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '20px',
            opacity: 0.5
          }}>
            📸
          </div>
          <div style={{ 
            fontSize: '20px', 
            fontWeight: 600, 
            marginBottom: '12px',
            color: '#1f2937'
          }}>
            No public photos yet
          </div>
          <div style={{ fontSize: '15px', lineHeight: '1.5' }}>
            The event organizer hasn't made any photos public yet.
            <br />
            Check back later or use the Search tab to find your photos.
          </div>
        </div>
      );
    }

    return (
      <div>
        {/* Gallery Header */}
        <div style={{ 
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: 600, 
                color: '#1f2937',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <PictureOutlined style={{ color: '#6f4cff' }} />
                Event Gallery
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#6b7280' 
              }}>
                {publicPhotos.length} public {publicPhotos.length === 1 ? 'photo' : 'photos'} available
              </div>
            </div>
            
            {/* Download All Photos Button */}
            {publicPhotos.some(photo => isPhotoReady(photo)) && (
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={handleDownloadAllGalleryPhotos}
                disabled={isDownloadingAllGallery}
                loading={isDownloadingAllGallery}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  height: '40px',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                Download All Photos
              </Button>
            )}
          </div>
        </div>

        {/* Photos Grid */}
        <GalleryGrid>
          <Image.PreviewGroup>
            {publicPhotos.map((photo, index) => {
              const thumbnailSrc = getThumbnailSource(photo);
              const statusDisplay = getProcessingStatusDisplay(photo);
              const photoReady = isPhotoReady(photo);
              
              return (
                <Card
                  key={photo.id}
                  hoverable={photoReady}
                  styles={{ body: {padding: 0} }}
                  cover={
                    <GalleryImageContainer ref={index === 0 ? firstGalleryImageRef : null}>
                      {thumbnailSrc ? (
                        <GalleryImage
                          src={thumbnailSrc}
                          alt={photo.filename || `Photo ${index + 1}`}
                          preview={photoReady ? { src: photo.url } : false}
                          style={{
                            filter: statusDisplay ? 'brightness(0.7)' : 'none'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '300px',
                          background: '#f5f5f5',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          borderRadius: '8px'
                        }}>
                          <PictureOutlined style={{ fontSize: '24px', color: '#8c8c8c', marginBottom: '8px' }} />
                          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            Image unavailable
                          </div>
                        </div>
                      )}
                      {statusDisplay && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          background: 'rgba(255, 255, 255, 0.9)',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                        }}>
                          <div style={{ color: statusDisplay.color, fontSize: '16px' }}>
                            {statusDisplay.icon}
                          </div>
                          <div style={{ color: statusDisplay.color, fontSize: '12px', marginTop: 4 }}>
                            {statusDisplay.text}
                          </div>
                        </div>
                      )}
                    </GalleryImageContainer>
                  }
                  style={{ 
                    overflow: 'hidden', 
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    border: '1px solid #f0f0f0',
                    opacity: statusDisplay ? 0.8 : 1
                  }}
                >
                  <div style={{ 
                    padding: 12, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    background: '#fafafa'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text style={{ fontSize: '12px', color: '#6f4cff', fontWeight: 600 }}>
                        Public Photo
                      </Text>
                      {statusDisplay && (
                        <Text style={{ fontSize: '10px', color: statusDisplay.color, marginTop: 2 }}>
                          {statusDisplay.text}
                        </Text>
                      )}
                    </div>
                    <Button 
                      type="primary" 
                      size="small" 
                      icon={<CloudDownloadOutlined />} 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPublicPhoto(photo);
                      }}
                      disabled={!photoReady}
                      style={{
                        background: photoReady ? 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)' : '#d9d9d9',
                        border: 'none',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                </Card>
              );
            })}
          </Image.PreviewGroup>
        </GalleryGrid>

        {/* Gallery Footer Info */}
        <div style={{ 
          marginTop: '32px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '12px',
          fontSize: '13px',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          These are publicly available photos from the event. 
          Use the Search tab to find photos that match your face.
        </div>
      </div>
    );
  };

  const renderEventContent = () => {
    
    if (isLoading) {
      return (
        <MobileContainer>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flex: 1,
            color: 'white'
          }}>
            <Spin size="large" />
            <Paragraph style={{ marginTop: '16px', color: 'white' }}>Loading event details...</Paragraph>
          </div>
        </MobileContainer>
      );
    }
    
    if (isError || !event) {
      return (
        <MobileContainer>
          <MobileHeader>
            <MobileBrand>Recognix</MobileBrand>
            <MobileSubtitle>Find your event using the code provided by the organizer</MobileSubtitle>
            <DesignerCredit>
              <a href="tel:+61418585811">Designed by Baruch Goberman</a>
              <CreditDivider>•</CreditDivider>
              <a href="tel:+61418585811">+614 18 585 811</a>
              <CreditDivider>•</CreditDivider>
              <a href="mailto:info@recognix.com.au">info@recognix.com.au</a>
            </DesignerCredit>
          </MobileHeader>

          <ContentCard>
            {isError && (
              <Alert
                message="Event Not Found"
                description="We couldn't find the event you're looking for. Please check your event code and try again."
                type="error"
                showIcon
                style={{ marginBottom: '24px' }}
              />
            )}
            
            <div style={{ marginBottom: '24px' }}>
              <Input 
                placeholder="Enter event code"
                value={eventCode}
                onChange={(e) => setEventCode(e.target.value)}
                size="large"
                style={{ marginBottom: '16px' }}
                onPressEnter={handleFindEvent}
              />
              <StyledActionButton 
                variant="primary"
                onClick={handleFindEvent}
                block
              >
                Find Your Photos
              </StyledActionButton>
            </div>
            
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              The event code is typically printed on your event ticket or provided by the event organizer.
            </Text>
          </ContentCard>
        </MobileContainer>
      );
    }

    // Use the same styled layout for desktop
    return (
      <MobileContainer>
        <MobileHeader>
          <MobileBrand>Recognix</MobileBrand>
          <MobileSubtitle>Upload a selfie to find similar photos in your gallery using AI facial recognition</MobileSubtitle>
          <DesignerCredit>
            <a href="tel:+61418585811">Designed by Baruch Goberman</a>
            <CreditDivider>•</CreditDivider>
            <a href="tel:+61418585811">+614 18 585 811</a>
            <CreditDivider>•</CreditDivider>
            <a href="mailto:info@recognix.com.au">info@recognix.com.au</a>
          </DesignerCredit>
        </MobileHeader>

        <TabContainer>
          <TabButton 
            active={activeTab === 'search'} 
            onClick={() => setActiveTab('search')}
          >
            Search
          </TabButton>
          <TabButton 
            active={activeTab === 'gallery'} 
            onClick={() => setActiveTab('gallery')}
          >
            Gallery
          </TabButton>
        </TabContainer>
        
        <ContentCard>
          {activeTab === 'search' ? (
            <>
              <PrivacyNotice>
                <PrivacyIcon>
                  <LockFilled />
                </PrivacyIcon>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckOutlined style={{ color: '#10b981', fontSize: '14px' }} />
                    Your Privacy is Protected
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '8px', lineHeight: '1.4' }}>
                    Your selfie is only used to match your event photos securely. It is not stored or viewed.
                  </div>
                  <div style={{ color: '#059669', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckOutlined style={{ fontSize: '12px' }} />
                    Private, instant results. Never shared for other purposes.
                  </div>
                </div>
              </PrivacyNotice>
              
              <UploadSection>
                {selfieUrl ? (
                  <div style={{ marginBottom: '20px' }}>
                    <img 
                      src={selfieUrl} 
                      alt="Your selfie" 
                      style={{ 
                        width: '100px', 
                        height: '100px', 
                        borderRadius: '50%', 
                        objectFit: 'cover',
                        border: '3px solid #e5e7eb',
                        display: 'block',
                        margin: '0 auto'
                      }} 
                    />
                    <Button 
                      type="link" 
                      onClick={handleRemoveImage}
                      style={{ marginTop: '12px', color: '#ef4444', fontSize: '14px' }}
                    >
                      Remove Photo
                    </Button>
                  </div>
                ) : (
                  <>
                    <StyledActionButton 
                      variant="primary"
                      icon={<UploadOutlined />}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/jpeg,image/png';
                        input.onchange = (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file) {
                            const rcFile = file as RcFile;
                            handleFileChange({ fileList: [{ originFileObj: rcFile }] as any });
                          }
                        };
                        input.click();
                      }}
                      block
                    >
                      Choose Photo
                    </StyledActionButton>
                    
                    <div style={{ margin: '20px 0' }}>
                      <div style={{ fontSize: '18px', fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
                        Add Your Photo
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        Choose from gallery or take a new photo to get started
                      </div>
                    </div>
                    
                    <StyledActionButton 
                      variant="secondary"
                      icon={<CameraOutlined />}
                      onClick={handleOpenCamera}
                      block
                    >
                      Take Photo
                    </StyledActionButton>
                  </>
                )}
                
                {selfieFile && (
                  <div style={{ marginTop: '20px' }}>
                    <StyledActionButton 
                      variant="primary"
                      onClick={handleFindPhotos}
                      loading={isMatching}
                      disabled={!selfieFile}
                      style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                      block
                    >
                      <PictureOutlined />
                      Find Your Photos
                    </StyledActionButton>
                  </div>
                )}
              </UploadSection>
            </>
          ) : (
            renderGalleryContent()
          )}
        </ContentCard>
      </MobileContainer>
    );
  };

  
  // Use the same layout for both mobile and desktop now
  return (
    <>
      {contextHolder}
      {renderEventContent()}
      {renderModals()}
    </>
  );
  
  // Directly render modals within components, don't use a separate function
  function renderModals() {
    return (
      <>
        {/* Modal for displaying matching photos */}
        <Modal
          title={null}
          open={isResultsModalVisible}
          onCancel={() => setIsResultsModalVisible(false)}
          width={isMobile ? '95vw' : 800}
          styles={{ 
            body: { padding: '0' },
            content: { 
              borderRadius: isMobile ? '16px' : '24px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)',
              margin: isMobile ? '10px' : 'auto'
            }
          }}
          style={{ top: isMobile ? 20 : 'auto' }}
          footer={null}
          closable={false}
        >
          {/* Header */}
          <div style={{ 
            padding: isMobile ? '20px 16px 20px 16px' : '24px 24px 16px 24px', 
            textAlign: 'center', 
            color: 'white',
            position: 'relative',
            minHeight: isMobile ? '80px' : 'auto'
          }}>
            {/* Close button */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsResultsModalVisible(false)}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                color: 'white',
                fontSize: isMobile ? '16px' : '18px',
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '0 16px 0 16px'
              }}
            />
            <div style={{ 
              fontSize: isMobile ? '20px' : '24px', 
              fontWeight: 700, 
              marginBottom: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <PictureOutlined />
              <div style={{ 
                textAlign: 'center',
                lineHeight: isMobile ? '1.2' : '1.4'
              }}>
                {isMobile ? (
                  <>
                    Your Matching<br />Photos
                  </>
                ) : (
                  'Your Matching Photos'
                )}
              </div>
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: 'rgba(255, 255, 255, 0.8)' 
            }}>
              We found {matchingPhotos.length} photos that match your selfie!
            </div>
          </div>

          {/* Content */}
          <div style={{ 
            background: 'white',
            margin: isMobile ? '0 16px' : '0 24px',
            borderRadius: '16px',
            padding: isMobile ? '20px 16px' : '24px',
            marginBottom: isMobile ? '16px' : '24px',
            maxHeight: isMobile ? '75vh' : '70vh',
            overflowY: 'auto'
          }}>
            {matchingPhotos.length > 0 ? (
              <>
                {/* Top Download Button */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginBottom: '24px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  gap: '16px'
                }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 600, 
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <CheckOutlined style={{ color: '#10b981' }} />
                    {matchingPhotos.length} photos found
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    gap: isMobile ? '8px' : '12px',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'stretch'
                  }}>
                    <Button 
                      type="primary"
                      onClick={() => {
                        setIsGalleryModalVisible(true);
                      }}
                      icon={<EyeOutlined />}
                      size="large"
                      style={{ 
                        background: 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)',
                        border: 'none',
                        height: isMobile ? '44px' : '48px',
                        borderRadius: '12px',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 600,
                        minWidth: isMobile ? 'auto' : '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        flex: isMobile ? '1' : 'none'
                      }}
                    >
                      View Gallery
                    </Button>
                    <Button 
                      type="primary"
                      onClick={handleDownloadAll}
                      icon={<CloudDownloadOutlined />}
                      disabled={matchingPhotos.length === 0}
                      size="large"
                      style={{ 
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        height: isMobile ? '44px' : '48px',
                        borderRadius: '12px',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 600,
                        minWidth: isMobile ? 'auto' : '160px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        flex: isMobile ? '1' : 'none'
                      }}
                    >
                      Download All
                    </Button>
                  </div>
                </div>

                {/* Photos Grid */}
                <Row gutter={[16, 16]}>
                  {matchingPhotos.map(photo => {
                    // Extract file name from URL (get the part after the last -)
                    const urlParts = photo.signedUrl.split('/');
                    const lastPart = urlParts[urlParts.length - 1];
                    const fileName = lastPart.split('?')[0].split('-').slice(1).join('-') || `Photo ${photo.itemId}`;
                    
                    return (
                      <Col xs={24} sm={12} md={8} key={photo.itemId}>
                        <Card
                          hoverable
                          styles={{ body: {padding: 0} }}
                          cover={
                            <Image
                              src={photo.thumbnailUrl ? photo.thumbnailUrl : photo.signedUrl}
                              alt={fileName}
                              style={{ width: '100%', height: 150, objectFit: 'cover' }}
                              preview={{ src: photo.signedUrl }}
                            />
                          }
                          style={{ 
                            overflow: 'hidden', 
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            border: '1px solid #f0f0f0'
                          }}
                        >
                          <div style={{ 
                            padding: 12, 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            background: '#fafafa'
                          }}>
                            <Text style={{ fontSize: '12px', color: '#6f4cff', fontWeight: 600 }}>
                              {photo.similarityScore.toFixed(0)}% match
                            </Text>
                            <Button 
                              type="primary" 
                              size="small" 
                              icon={<CloudDownloadOutlined />} 
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent opening the preview
                                handleDownloadPhoto(photo);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)',
                                border: 'none',
                                borderRadius: '8px'
                              }}
                            />
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>

                {/* Bottom Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '32px',
                  paddingTop: '24px',
                  borderTop: '1px solid #f0f0f0',
                  justifyContent: 'space-between'
                }}>
                  <Button 
                    onClick={() => setIsResultsModalVisible(false)}
                    style={{
                      height: '40px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 500,
                      minWidth: '80px',
                      flex: '0 0 auto'
                    }}
                  >
                    Close
                  </Button>
                  <Button 
                    type="primary"
                    onClick={handleDownloadAll}
                    icon={<CloudDownloadOutlined />}
                    disabled={matchingPhotos.length === 0}
                    style={{ 
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      height: '40px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: 600,
                      minWidth: '120px',
                      maxWidth: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      flex: '0 0 auto'
                    }}
                  >
                    Download All
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '48px 24px',
                color: '#6b7280'
              }}>
                <div style={{ 
                  fontSize: '48px', 
                  marginBottom: '16px',
                  opacity: 0.5
                }}>
                  📷
                </div>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 600, 
                  marginBottom: '8px',
                  color: '#1f2937'
                }}>
                  No matching photos found
                </div>
                <div style={{ fontSize: '14px' }}>
                  Try uploading a clearer selfie or check if you're at the right event
                </div>
                <Button 
                  onClick={() => setIsResultsModalVisible(false)}
                  size="large"
                  style={{
                    marginTop: '24px',
                    height: '48px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 500,
                    minWidth: '120px'
                  }}
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </Modal>

        {/* Camera Modal */}
        <Modal
          title={null}
          open={isCameraModalVisible}
          onCancel={handleCloseCamera}
          width={600}
          styles={{ 
            body: { padding: '0' },
            content: { 
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)'
            }
          }}
          style={{ top: isMobile ? 50: 90 }}
          footer={null}
          closable={false}
        >
          {/* Header */}
          <div style={{ 
            padding: '24px 24px 16px 24px', 
            textAlign: 'center', 
            color: 'white' 
          }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 700, 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <CameraOutlined />
              Take a Selfie
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: 'rgba(255, 255, 255, 0.8)' 
            }}>
              Position your face within the oval and take your photo
            </div>
          </div>

          {/* Camera Content */}
          <div style={{ 
            background: 'white',
            margin: '0 24px',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              position: 'relative', 
              width: '100%', 
              maxWidth: '400px',
              margin: '0 auto',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              <video 
                ref={videoRef}
                autoPlay 
                playsInline // important for iOS
                style={{ 
                  width: '100%', 
                  transform: 'scaleX(-1)' // mirror for selfie mode
                }}
              />
              
              {/* Capture guide overlay */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: isMobile ? '65%' : '50%',
                  height: isMobile ? '65%' : '75%',
                  border: '3px dashed white',
                  borderRadius: '50% 50% 50% 50% / 40% 40% 60% 60%',
                  pointerEvents: 'none',
                  boxShadow: '0 0 0 2000px rgba(0, 0, 0, 0.4)',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                }}
              />
            </div>
            
            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '24px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button 
                onClick={handleCloseCamera}
                size="large"
                style={{
                  flex: 1,
                  height: '48px',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 500
                }}
              >
                Cancel
              </Button>
              <StyledActionButton 
                variant="secondary"
                onClick={handleCapturePhoto}
                icon={<CameraOutlined />}
                style={{ 
                  flex: 1,
                  height: '48px',
                  fontSize: '16px'
                }}
              >
                Take Photo
              </StyledActionButton>
            </div>
            
            {/* Hidden canvas for processing the image */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </Modal>

        {/* Gallery Modal */}
        <Modal
          title={null}
          open={isGalleryModalVisible}
          onCancel={() => setIsGalleryModalVisible(false)}
          width={900}
          styles={{ 
            body: { padding: '0' },
            content: { 
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)'
            }
          }}
          footer={null}
          closable={false}
        >
          {/* Header */}
          <div style={{ 
            padding: '24px 24px 16px 24px', 
            textAlign: 'center', 
            color: 'white',
            position: 'relative'
          }}>
            {/* Close button */}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsGalleryModalVisible(false)}
              style={{
                position: 'absolute',
                top: '24px',
                right: '24px',
                color: 'white',
                fontSize: '18px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 700, 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <PictureOutlined />
              Event Gallery
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: 'rgba(255, 255, 255, 0.8)' 
            }}>
              {publicPhotos?.length || 0} photos from {event?.name || 'this event'}
            </div>
          </div>

          {/* Content */}
          <div style={{ 
            background: 'white',
            margin: '0 24px',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}>
            {publicPhotos && publicPhotos.length > 0 ? (
              <>
                {/* Gallery Grid */}
                <Row gutter={[16, 16]}>
                  <Image.PreviewGroup>
                    {publicPhotos.map((photo, index) => {
                      const thumbnailSrc = getThumbnailSource(photo);
                      const statusDisplay = getProcessingStatusDisplay(photo);
                      const photoReady = isPhotoReady(photo);
                      
                      return (
                        <Col xs={24} sm={12} md={8} key={photo.id}>
                          <Card
                            hoverable={photoReady}
                            styles={{ body: {padding: 0} }}
                            cover={
                              <div style={{ 
                                width: '100%', 
                                height: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#f8f9fa',
                                position: 'relative'
                              }}>
                                {thumbnailSrc ? (
                                  <Image
                                    src={thumbnailSrc}
                                    alt={photo.filename || `Photo ${index + 1}`}
                                    preview={photoReady ? { src: photo.url } : false}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      filter: statusDisplay ? 'brightness(0.7)' : 'none'
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#8c8c8c'
                                  }}>
                                    <PictureOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                                    <div style={{ fontSize: '12px' }}>
                                      Image unavailable
                                    </div>
                                  </div>
                                )}
                                {statusDisplay && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    background: 'rgba(255, 255, 255, 0.9)',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                  }}>
                                    <div style={{ color: statusDisplay.color, fontSize: '16px' }}>
                                      {statusDisplay.icon}
                                    </div>
                                    <div style={{ color: statusDisplay.color, fontSize: '12px', marginTop: 4 }}>
                                      {statusDisplay.text}
                                    </div>
                                  </div>
                                )}
                              </div>
                            }
                            style={{ 
                              overflow: 'hidden', 
                              borderRadius: '12px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.3s ease',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            <div style={{ 
                              padding: 12, 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              background: '#fafafa'
                            }}>
                              <Text style={{ fontSize: '12px', color: '#6f4cff', fontWeight: 600 }}>
                                Public Photo
                              </Text>
                              <Button 
                                type="primary" 
                                size="small" 
                                icon={<CloudDownloadOutlined />} 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPublicPhoto(photo);
                                }}
                                disabled={!photoReady}
                                style={{
                                  background: photoReady ? 'linear-gradient(135deg, #6f4cff 0%, #4c3e9f 100%)' : '#d9d9d9',
                                  border: 'none',
                                  borderRadius: '8px'
                                }}
                              />
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Image.PreviewGroup>
                </Row>

                {/* Download All Button */}
                {publicPhotos.some(photo => isPhotoReady(photo)) && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    marginTop: '32px',
                    paddingTop: '24px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <Button
                      type="primary"
                      icon={<CloudDownloadOutlined />}
                      onClick={handleDownloadAllGalleryPhotos}
                      disabled={isDownloadingAllGallery}
                      loading={isDownloadingAllGallery}
                      size="large"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        height: '48px',
                        fontSize: '16px',
                        fontWeight: 600,
                        minWidth: '200px'
                      }}
                    >
                      Download All Photos
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#6b7280'
              }}>
                <div style={{ 
                  fontSize: '48px', 
                  marginBottom: '20px',
                  opacity: 0.5
                }}>
                  📸
                </div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  marginBottom: '12px',
                  color: '#1f2937'
                }}>
                  No photos available
                </div>
                <div style={{ fontSize: '15px', lineHeight: '1.5' }}>
                  The event organizer hasn't uploaded any photos yet.
                  <br />
                  Check back later!
                </div>
              </div>
            )}
          </div>
        </Modal>
      </>
    );
  }
};

export default EventPage;